import 'regenerator-runtime/runtime';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';

import React, { useEffect, ReactNode } from 'react';
import { SWRConfig } from 'swr';
import { NextPage } from 'next';
import App, { AppProps } from 'next/app';
import { useRouter } from 'next/router';

import { BdsProvider } from '@bucketplace/design-system';

import { UserContextProvider, SearchQueryContextProvider } from 'provider';
import { fetcher } from 'api/fetcher';
import { setUtmData, saveAppOsInfo, sendA1MediaInflow } from 'utils';

import { Layout, PageLayout } from 'components/Layout';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { Error } from 'components/Error';

import 'styles/global.css';

type Page<P = Record<string, never>> = NextPage<P> & {
  getLayout?: (page:ReactNode) => ReactNode,
};

type MyAppProps = AppProps & {
  Component: Page,
};

function MyApp ({ Component, pageProps } : MyAppProps): React.ReactElement {
  const router = useRouter();
  useEffect(() => {
    setUtmData();
    saveAppOsInfo();
    sendA1MediaInflow();

    router.events.on('routeChangeComplete', setUtmData);
    return () => router.events.off('routeChangeComplete', setUtmData);
  }, [router.events]);

  const getLayout = Component.getLayout || ((page:ReactNode) => page);
  return (
    <BdsProvider mode="light">
      <SWRConfig
        value={{
          revalidateOnFocus: false,
          fetcher,
        }}
      >
        <SearchQueryContextProvider>
          <UserContextProvider>
            <Layout>
              <PageLayout>
                <ErrorBoundary fallback={<Error />}>
                  { getLayout(<Component {...pageProps} />) }
                </ErrorBoundary>
              </PageLayout>
            </Layout>
          </UserContextProvider>
        </SearchQueryContextProvider>
      </SWRConfig>
    </BdsProvider>
  );
}

MyApp.getInitialProps = async (context) => {
  const appProps = await App.getInitialProps(context);
  return { ...appProps };
};

export default MyApp;
